import {
  collection,
  doc,
  getDoc,
  getDocs,
  addDoc,
  updateDoc,
  deleteDoc,
  query,
  where,
  orderBy,
  serverTimestamp
} from 'firebase/firestore'
import { db } from '../firebase'
import { updateUserProfile } from './userService'
import { deleteAssignmentsForTeacherClass } from './assignmentService'

const TEACHER_CLASSES_COLLECTION = 'teacherClasses'

/**
 * Génère un code court unique (lettres + chiffres, 8 caractères)
 */
export function generateCode() {
  const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789'
  let code = ''
  for (let i = 0; i < 8; i++) {
    code += chars.charAt(Math.floor(Math.random() * chars.length))
  }
  return code
}

/**
 * Crée une classe pour un professeur
 * @param {string} teacherId - uid du professeur
 * @param {string} name - nom affiché de la classe (ex. "1ère S – Groupe A")
 * @param {string} [code] - code optionnel ; si absent, un code unique est généré
 * @param {string|null} [establishment] - établissement (liste référence) ; copié sur le profil élève à la jointure
 * @returns {Promise<{ id: string, name: string, code: string }>}
 */
export async function createTeacherClass(teacherId, name, code = null, establishment = null) {
  const trimmedName = (name || '').trim()
  if (!trimmedName) throw new Error('Veuillez saisir un nom pour la classe.')

  let finalCode = (code || '').trim().toUpperCase()
  if (!finalCode) finalCode = generateCode()

  const existing = await getTeacherClassByCode(finalCode)
  if (existing) {
    if ((code || '').trim()) throw new Error('Ce code est déjà utilisé. Choisissez un autre code.')
    finalCode = generateCode()
    const retry = await getTeacherClassByCode(finalCode)
    if (retry) finalCode = finalCode + '-' + Date.now().toString(36).slice(-4)
  }

  const trimmedEst = typeof establishment === 'string' ? establishment.trim() : ''

  const ref = await addDoc(collection(db, TEACHER_CLASSES_COLLECTION), {
    teacherId,
    name: trimmedName,
    code: finalCode,
    establishment: trimmedEst || null,
    createdAt: serverTimestamp()
  })

  return { id: ref.id, name: trimmedName, code: finalCode }
}

/**
 * Liste les classes d'un professeur
 * @param {string} teacherId - uid du professeur
 * @returns {Promise<Array<{ id: string, teacherId: string, name: string, code: string, createdAt }>>}
 */
export async function getTeacherClasses(teacherId) {
  const q = query(
    collection(db, TEACHER_CLASSES_COLLECTION),
    where('teacherId', '==', teacherId),
    orderBy('createdAt', 'desc')
  )
  const snapshot = await getDocs(q)
  const list = []
  snapshot.forEach((d) => {
    list.push({ id: d.id, ...d.data() })
  })
  return list
}

/**
 * Récupère une classe par son id
 * @param {string} classId - id du document teacherClasses
 */
export async function getTeacherClassById(classId) {
  const ref = doc(db, TEACHER_CLASSES_COLLECTION, classId)
  const snap = await getDoc(ref)
  if (!snap.exists()) return null
  return { id: snap.id, ...snap.data() }
}

/**
 * Récupère une classe par son code (insensible à la casse)
 * @param {string} code - code saisi par l'élève
 */
export async function getTeacherClassByCode(code) {
  const normalized = (code || '').trim().toUpperCase()
  if (!normalized) return null
  const q = query(
    collection(db, TEACHER_CLASSES_COLLECTION),
    where('code', '==', normalized)
  )
  const snapshot = await getDocs(q)
  if (snapshot.empty) return null
  const d = snapshot.docs[0]
  return { id: d.id, ...d.data() }
}

/**
 * Met à jour une classe (nom, établissement, code) — propriétaire uniquement.
 * Changer le code invalide l’ancien lien de rejoindre ; les élèves déjà joints restent liés par teacherClassId.
 */
export async function updateTeacherClass(classId, teacherId, data) {
  const ref = doc(db, TEACHER_CLASSES_COLLECTION, classId)
  const snap = await getDoc(ref)
  if (!snap.exists() || snap.data().teacherId !== teacherId) {
    throw new Error('Classe introuvable ou vous n\'êtes pas le propriétaire.')
  }
  const updates = { updatedAt: serverTimestamp() }
  if (data.name !== undefined) {
    const trimmedName = (data.name || '').trim()
    if (!trimmedName) throw new Error('Veuillez saisir un nom pour la classe.')
    updates.name = trimmedName
  }
  if (data.establishment !== undefined) {
    const e = typeof data.establishment === 'string' ? data.establishment.trim() : ''
    updates.establishment = e || null
  }
  if (data.code !== undefined) {
    const finalCode = (data.code || '').trim().toUpperCase()
    if (!finalCode) throw new Error('Veuillez saisir un code pour la classe.')
    const current = (snap.data().code || '').toUpperCase()
    if (finalCode !== current) {
      const existing = await getTeacherClassByCode(finalCode)
      if (existing && existing.id !== classId) {
        throw new Error('Ce code est déjà utilisé. Choisissez un autre code.')
      }
      updates.code = finalCode
    }
  }
  await updateDoc(ref, updates)
}

/**
 * Supprime une classe (le propriétaire uniquement) et tous les devoirs associés.
 * Les élèves gardent teacherId/teacherClassId jusqu'à ce qu'ils changent de classe.
 */
export async function deleteTeacherClass(classId, teacherId) {
  const ref = doc(db, TEACHER_CLASSES_COLLECTION, classId)
  const snap = await getDoc(ref)
  if (!snap.exists() || snap.data().teacherId !== teacherId) {
    throw new Error('Classe introuvable ou vous n\'êtes pas le propriétaire.')
  }
  await deleteAssignmentsForTeacherClass(teacherId, classId)
  await deleteDoc(ref)
}

/**
 * Fait rejoindre une classe à un élève via le code
 * @param {string} userId - uid de l'élève
 * @param {string} code - code de la classe
 * @returns {Promise<{ name: string }>} - nom de la classe rejointe
 */
export async function joinTeacherClass(userId, code) {
  const tc = await getTeacherClassByCode(code)
  if (!tc) throw new Error('Code invalide. Vérifiez le code fourni par votre professeur.')

  const establishment =
    tc.establishment && String(tc.establishment).trim() ? String(tc.establishment).trim() : null

  await updateUserProfile(userId, {
    teacherClassId: tc.id,
    teacherId: tc.teacherId,
    establishment,
    class: (tc.name && String(tc.name).trim()) || null
  })
  return { name: tc.name }
}

/**
 * Fait quitter la classe de l'élève
 * @param {string} userId - uid de l'élève
 */
export async function leaveTeacherClass(userId) {
  await updateUserProfile(userId, {
    teacherClassId: null,
    teacherId: null,
    establishment: null,
    class: null
  })
}
